import { CircularProgress } from '@mui/material';
import { Building2, User } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getIndicacaoIndicador, postIndicacaoVisita } from '../../../services/indicacao';
import { useToastStore } from '../../../stores';
import { maskCep, maskDocumento, maskTelefone } from '../../../utils/mask/MaskUtils';
import { ESTADOS, getCidadesByUfId } from '../data/brasilLocalidades';
import { fetchCep, registerUser } from '../../authentication/repository/repository';
import { RegisterPayload, RegisterType } from '../../authentication/types/types';
import { ArrowIcon } from '../../public-home/components/icons';

const normalizeText = (value: string) =>
	value
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toUpperCase()
		.trim();

/**
 * Cadastro público de revenda no card da página /cadastro e /indicacao.
 * Mesma regra do CadastroPublico: cria a conta em CreateRevendaExterno,
 * com ou sem indicação. O código do deeplink (?ref=) trava o campo,
 * registra a visita e mostra o indicador.
 */
const IndicacaoForm: React.FC = () => {
	const { setToast } = useToastStore();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const refCodigo = (searchParams.get('ref') ?? '').trim();

	const [loading, setLoading] = useState(false);
	const [indicadorNome, setIndicadorNome] = useState('');
	const cepRequestRef = useRef(0);

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
	} = useForm<RegisterType>({
		defaultValues: {
			pessoa_fisica: true,
			documento: '',
			razao_social: '',
			fantasia: '',
			ie: null,
			im: null,
			responsavel: null,
			telefone: null,
			celular: null,
			cep: null,
			logradouro: null,
			numero: null,
			complemento: null,
			bairro: null,
			uf: '',
			cidade: '',
			indicacao: null,
			email: null,
			senha: null,
			confirmar_senha: null,
			aceite_termos: false,
		},
	});

	const senha = watch('senha');
	const cep = watch('cep');
	const uf = watch('uf');
	const pessoaFisica = watch('pessoa_fisica');
	const isFirstRender = useRef(true);
	const cidades = useMemo(() => {
		const estadoId = ESTADOS.find((item) => item.uf === uf)?.id;
		if (!estadoId) return [];
		return getCidadesByUfId(estadoId);
	}, [uf]);

	useEffect(() => {
		if (!refCodigo) return;
		setValue('indicacao', refCodigo);

		const visitaKey = `indicacao_visita_${refCodigo.toUpperCase()}`;
		if (!sessionStorage.getItem(visitaKey)) {
			sessionStorage.setItem(visitaKey, '1');
			postIndicacaoVisita(refCodigo).catch(() => {
				sessionStorage.removeItem(visitaKey);
			});
		}

		getIndicacaoIndicador(refCodigo)
			.then((response) => {
				const nome = response?.data?.nome;
				if (nome) setIndicadorNome(nome);
				else setIndicadorNome('');
			})
			.catch(() => setIndicadorNome(''));
	}, [refCodigo, setValue]);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		setValue('documento', '');
		if (pessoaFisica) {
			setValue('ie', null);
			setValue('im', null);
		}
	}, [pessoaFisica, setValue]);

	useEffect(() => {
		const cleanCep = cep?.replace(/\D/g, '') || '';
		if (cleanCep.length !== 8) return;

		const requestId = ++cepRequestRef.current;
		const handleFetchCep = async () => {
			const data = await fetchCep(cleanCep);
			if (requestId !== cepRequestRef.current) return;

			if (data?.message) {
				setToast({
					message: typeof data.message === 'string' ? data.message : 'CEP não encontrado.',
					variant: 'error',
				});
				return;
			}

			const ufCep = String(data.state || '').trim().toUpperCase();
			setValue('logradouro', data.street || '');
			setValue('bairro', data.neighborhood || '');
			setValue('uf', ufCep);

			const estadoId = ESTADOS.find((item) => item.uf === ufCep)?.id;
			const lista = estadoId ? getCidadesByUfId(estadoId) : [];
			const cidadeApi = normalizeText(data.city || '');
			const cidadeMatch = lista.find((item) => normalizeText(item.descricao) === cidadeApi);
			setValue('cidade', cidadeMatch?.descricao || '');
		};

		handleFetchCep();
	}, [cep, setToast, setValue]);

	const ufRegister = register('uf', { required: 'UF é obrigatória' });

	const onSubmit = async (data: RegisterType) => {
		setLoading(true);
		const payload: RegisterPayload = {
			bairro: data.bairro?.trim() ?? '',
			cadastroExterno: '1',
			celular: data.celular?.trim() ?? '',
			cep: data.cep?.trim() ?? '',
			cidade: data.cidade?.trim() ?? '',
			complementoLogradouro: data.complemento?.trim() ?? '',
			email: data.email?.trim() ?? '',
			estado: data.uf?.trim() ?? '',
			fantasia: data.fantasia?.trim() ?? '',
			indicacao: (data.indicacao?.trim() || refCodigo).trim(),
			inscricaoEstadual: data.pessoa_fisica ? '' : (data.ie?.trim() ?? ''),
			inscricaoMunicipal: data.pessoa_fisica ? '' : (data.im?.trim() ?? ''),
			logotipo: '',
			logradouro: data.logradouro?.trim() ?? '',
			numeroDocumento: data.documento?.trim() ?? '',
			numeroLogradouro: data.numero?.trim() ?? '',
			observacao: '',
			razaoSocial: data.razao_social?.trim() ?? '',
			responsavel: data.responsavel?.trim() ?? '',
			senha: data.senha?.trim() ?? '',
			tagAtivo: '1',
			tagDeferimento: '0',
			tagRecebeEmail: '1',
			telefone: data.telefone?.trim() ?? '',
			tipoDocumento: data.pessoa_fisica ? '1' : '2',
		};

		try {
			const response = await registerUser(payload);
			if (response.toLowerCase().includes('cadastrada com sucesso')) {
				setToast({ message: 'Cadastro realizado com sucesso!', variant: 'success' });
				navigate('/login');
			} else {
				setToast({ message: response, variant: 'error' });
			}
		} catch {
			setToast({ message: 'Erro ao realizar cadastro. Tente novamente.', variant: 'error' });
		} finally {
			setLoading(false);
		}
	};

	return (
		<form className='cr-ind-form__fields' onSubmit={handleSubmit(onSubmit)} noValidate>
			<div className='cr-ind-block'>
				<p className='cr-ind-block__title'>Dados da empresa</p>
				<div className='cr-group'>
					<span className='cr-label' id='lblTipo'>
						Você é:
					</span>
					<div className='cr-tipo' role='group' aria-labelledby='lblTipo'>
						<button
							type='button'
							className={`cr-tipo__btn${pessoaFisica ? ' is-active' : ''}`}
							aria-pressed={pessoaFisica}
							onClick={() => setValue('pessoa_fisica', true)}
							disabled={loading}
						>
							<User aria-hidden='true' strokeWidth={2} />
							Pessoa Física
						</button>
						<button
							type='button'
							className={`cr-tipo__btn${!pessoaFisica ? ' is-active' : ''}`}
							aria-pressed={!pessoaFisica}
							onClick={() => setValue('pessoa_fisica', false)}
							disabled={loading}
						>
							<Building2 aria-hidden='true' strokeWidth={2} />
							Pessoa Jurídica
						</button>
					</div>
				</div>

				<div className='cr-group'>
					<label className='cr-label' htmlFor='ind-documento'>
						{pessoaFisica ? 'CPF *' : 'CNPJ *'}
					</label>
					<input
						id='ind-documento'
						className='cr-field'
						type='text'
						inputMode='numeric'
						placeholder={pessoaFisica ? 'CPF *' : 'CNPJ *'}
						autoComplete='off'
						{...register('documento', {
							required: 'Documento é obrigatório',
							onChange: (e) => {
								e.target.value = maskDocumento(e.target.value, pessoaFisica);
							},
						})}
						disabled={loading}
					/>
					{errors.documento && <span className='cr-error'>{errors.documento.message}</span>}
				</div>

				<div className='cr-ind-row cr-ind-row--2'>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='ind-razao'>
							{pessoaFisica ? 'Nome *' : 'Razão Social *'}
						</label>
						<input
							id='ind-razao'
							className='cr-field'
							type='text'
							placeholder={pessoaFisica ? 'Nome *' : 'Razão Social *'}
							autoComplete={pessoaFisica ? 'given-name' : 'organization'}
							{...register('razao_social', {
								required: pessoaFisica ? 'Nome é obrigatório' : 'Razão Social é obrigatória',
							})}
							disabled={loading}
						/>
						{errors.razao_social && <span className='cr-error'>{errors.razao_social.message}</span>}
					</div>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='ind-fantasia'>
							{pessoaFisica ? 'Sobrenome *' : 'Nome Fantasia *'}
						</label>
						<input
							id='ind-fantasia'
							className='cr-field'
							type='text'
							placeholder={pessoaFisica ? 'Sobrenome *' : 'Nome Fantasia *'}
							autoComplete={pessoaFisica ? 'family-name' : 'off'}
							{...register('fantasia', {
								required: pessoaFisica ? 'Sobrenome é obrigatório' : 'Nome Fantasia é obrigatório',
							})}
							disabled={loading}
						/>
						{errors.fantasia && <span className='cr-error'>{errors.fantasia.message}</span>}
					</div>
				</div>

				{!pessoaFisica && (
					<div className='cr-ind-row cr-ind-row--2'>
						<div className='cr-group'>
							<label className='cr-label' htmlFor='ind-ie'>
								Inscrição Estadual
							</label>
							<input
								id='ind-ie'
								className='cr-field'
								type='text'
								placeholder='Inscrição Estadual'
								autoComplete='off'
								{...register('ie')}
								disabled={loading}
							/>
						</div>
						<div className='cr-group'>
							<label className='cr-label' htmlFor='ind-im'>
								Inscrição Municipal
							</label>
							<input
								id='ind-im'
								className='cr-field'
								type='text'
								placeholder='Inscrição Municipal'
								autoComplete='off'
								{...register('im')}
								disabled={loading}
							/>
						</div>
					</div>
				)}
			</div>

			<div className='cr-ind-block'>
				<p className='cr-ind-block__title'>Contato</p>
				<div className='cr-group'>
					<label className='cr-label' htmlFor='ind-responsavel'>
						Responsável *
					</label>
					<input
						id='ind-responsavel'
						className='cr-field'
						type='text'
						placeholder='Responsável *'
						autoComplete='name'
						{...register('responsavel', { required: 'Responsável é obrigatório' })}
						disabled={loading}
					/>
					{errors.responsavel && <span className='cr-error'>{errors.responsavel.message}</span>}
				</div>
				<div className='cr-ind-row cr-ind-row--2'>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='ind-telefone'>
							Contato1 *
						</label>
						<input
							id='ind-telefone'
							className='cr-field'
							type='tel'
							inputMode='tel'
							placeholder='Contato1 *'
							autoComplete='tel'
							{...register('telefone', {
								required: 'Contato1 é obrigatório',
								onChange: (e) => {
									e.target.value = maskTelefone(e.target.value);
								},
							})}
							disabled={loading}
						/>
						{errors.telefone && <span className='cr-error'>{errors.telefone.message}</span>}
					</div>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='ind-celular'>
							Contato2 *
						</label>
						<input
							id='ind-celular'
							className='cr-field'
							type='tel'
							inputMode='tel'
							placeholder='Contato2 *'
							autoComplete='tel'
							{...register('celular', {
								required: 'Contato2 é obrigatório',
								onChange: (e) => {
									e.target.value = maskTelefone(e.target.value);
								},
							})}
							disabled={loading}
						/>
						{errors.celular && <span className='cr-error'>{errors.celular.message}</span>}
					</div>
				</div>
			</div>

			<div className='cr-ind-block'>
				<p className='cr-ind-block__title'>Endereço</p>
				<div className='cr-ind-row cr-ind-row--cep'>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='ind-cep'>
							CEP *
						</label>
						<input
							id='ind-cep'
							className='cr-field'
							type='text'
							inputMode='numeric'
							placeholder='CEP *'
							autoComplete='postal-code'
							{...register('cep', {
								required: 'CEP é obrigatório',
								onChange: (e) => {
									e.target.value = maskCep(e.target.value);
								},
							})}
							disabled={loading}
						/>
						{errors.cep && <span className='cr-error'>{errors.cep.message}</span>}
					</div>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='ind-logradouro'>
							Logradouro *
						</label>
						<input
							id='ind-logradouro'
							className='cr-field'
							type='text'
							placeholder='Logradouro *'
							autoComplete='address-line1'
							{...register('logradouro', { required: 'Logradouro é obrigatório' })}
							disabled={loading}
						/>
						{errors.logradouro && <span className='cr-error'>{errors.logradouro.message}</span>}
					</div>
				</div>
				<div className='cr-ind-row cr-ind-row--2'>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='ind-numero'>
							Número *
						</label>
						<input
							id='ind-numero'
							className='cr-field'
							type='text'
							placeholder='Número *'
							autoComplete='off'
							{...register('numero', { required: 'Número é obrigatório' })}
							disabled={loading}
						/>
						{errors.numero && <span className='cr-error'>{errors.numero.message}</span>}
					</div>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='ind-complemento'>
							Complemento
						</label>
						<input
							id='ind-complemento'
							className='cr-field'
							type='text'
							placeholder='Complemento'
							autoComplete='address-line2'
							{...register('complemento')}
							disabled={loading}
						/>
					</div>
				</div>
				<div className='cr-group'>
					<label className='cr-label' htmlFor='ind-bairro'>
						Bairro *
					</label>
					<input
						id='ind-bairro'
						className='cr-field'
						type='text'
						placeholder='Bairro *'
						autoComplete='off'
						{...register('bairro', { required: 'Bairro é obrigatório' })}
						disabled={loading}
					/>
					{errors.bairro && <span className='cr-error'>{errors.bairro.message}</span>}
				</div>
				<div className='cr-ind-row cr-ind-row--uf'>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='ind-uf'>
							UF *
						</label>
						<select
							id='ind-uf'
							className='cr-field'
							{...ufRegister}
							onChange={(event) => {
								ufRegister.onChange(event);
								setValue('cidade', '');
							}}
							disabled={loading}
						>
							<option value=''>Selecione...</option>
							{ESTADOS.map((estado) => (
								<option key={estado.id} value={estado.uf}>
									{estado.uf} — {estado.descricao}
								</option>
							))}
						</select>
						{errors.uf && <span className='cr-error'>{errors.uf.message}</span>}
					</div>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='ind-cidade'>
							Cidade *
						</label>
						<select
							id='ind-cidade'
							className='cr-field'
							{...register('cidade', { required: 'Cidade é obrigatória' })}
							disabled={loading || !uf}
						>
							<option value=''>{uf ? 'Selecione...' : 'Selecione o estado primeiro...'}</option>
							{cidades.map((cidade) => (
								<option key={cidade.id} value={cidade.descricao}>
									{cidade.descricao}
								</option>
							))}
						</select>
						{errors.cidade && <span className='cr-error'>{errors.cidade.message}</span>}
					</div>
				</div>
			</div>

			<div className='cr-ind-block'>
				<p className='cr-ind-block__title'>Acesso</p>
				<div className='cr-group'>
					<label className='cr-label' htmlFor='ind-indicacao'>
						Indicação
					</label>
					<input
						id='ind-indicacao'
						className='cr-field'
						type='text'
						placeholder='Indicação'
						autoComplete='off'
						{...register('indicacao')}
						disabled={loading || refCodigo !==null || refCodigo ===null}
						readOnly={!!refCodigo}
					/>
					{refCodigo && indicadorNome && <span className='cr-ind-hint'>Indicador: {indicadorNome}</span>}
					{refCodigo && !indicadorNome && (
						<span className='cr-ind-hint'>Código de indicação aplicado.</span>
					)}
				</div>
				<div className='cr-group'>
					<label className='cr-label' htmlFor='ind-email'>
						E-mail *
					</label>
					<input
						id='ind-email'
						className='cr-field'
						type='email'
						inputMode='email'
						placeholder='E-mail *'
						autoComplete='email'
						{...register('email', {
							required: 'E-mail é obrigatório',
							pattern: {
								value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
								message: 'E-mail inválido',
							},
						})}
						disabled={loading}
					/>
					{errors.email && <span className='cr-error'>{errors.email.message}</span>}
				</div>
				<div className='cr-ind-row cr-ind-row--2'>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='ind-senha'>
							Senha *
						</label>
						<input
							id='ind-senha'
							className='cr-field'
							type='password'
							placeholder='Senha *'
							autoComplete='new-password'
							{...register('senha', {
								required: 'Senha é obrigatória',
								minLength: { value: 6, message: 'Mínimo 6 caracteres' },
							})}
							disabled={loading}
						/>
						{errors.senha && <span className='cr-error'>{errors.senha.message}</span>}
					</div>
					<div className='cr-group'>
						<label className='cr-label' htmlFor='ind-confirmar'>
							Confirmar senha *
						</label>
						<input
							id='ind-confirmar'
							className='cr-field'
							type='password'
							placeholder='Confirmar senha *'
							autoComplete='new-password'
							{...register('confirmar_senha', {
								required: 'Confirmação de senha é obrigatória',
								validate: (value) => value === senha || 'As senhas não conferem',
							})}
							disabled={loading}
						/>
						{errors.confirmar_senha && (
							<span className='cr-error'>{errors.confirmar_senha.message}</span>
						)}
					</div>
				</div>
			</div>

			<label className='cr-check'>
				<input
					type='checkbox'
					{...register('aceite_termos', {
						validate: (value) => value === true || 'É necessário aceitar os Termos de Uso',
					})}
					disabled={loading}
				/>
				<span>Aceito os Termos de Uso</span>
			</label>
			{errors.aceite_termos && <span className='cr-error'>{errors.aceite_termos.message}</span>}

			<div className='cr-ind-submit-wrap'>
				<button type='submit' className='cr-ind-submit' disabled={loading}>
					{loading ? (
						<CircularProgress size={18} color='inherit' />
					) : (
						<>
							Cadastrar
							<ArrowIcon />
						</>
					)}
				</button>
			</div>
			<p className='cr-ind-form__lgpd'>*Campos de preenchimento obrigatório.</p>
		</form>
	);
};

export default IndicacaoForm;
